import React, { useEffect, useMemo, useRef, useState } from "react";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { User } from "../../../services/restserver-openapi";
import type { StayDetail } from "../../../services/restserver-openapi/models/StayDetail";

import ItineraryHeaderControls, { SortDirection } from "./TourItineraryHeaderControls";

import DistanceMarker from "../../../components/vanliferComponents/Community/StoriesView/DistanceMarker";
import StayProposalTimelineItem from "../../../components/vanliferComponents/StayProposalTimelineItem";
import CustomStayTimelineItem from "../../../components/vanliferComponents/CustomStayTimelineItem";
import CustomStayDialog from "../../../components/vanliferComponents/CustomStayDialog";
import ConfirmDialog from "../../../components/vanliferComponents/Community/Dialogs/ConfirmDialog";
import { asyncAddCustomStay, asyncDeleteCustomStay, asyncUpdateCustomStay } from "../../../store/Vanlifer/tourSlice";

const CUSTOM_STAY_TYPE = {
    HOST: "HOST" as StayDetail.stayType,
    CUSTOM_START: "CUSTOM_START" as StayDetail.stayType,
    CUSTOM_STOP: "CUSTOM_STOP" as StayDetail.stayType,
    CUSTOM_END: "CUSTOM_END" as StayDetail.stayType,
};

function isCustomStayRecord(stay?: StayDetail): boolean {
    if (!stay) {
        return false;
    }

    if (stay.stayType != null && stay.stayType !== CUSTOM_STAY_TYPE.HOST) {
        return true;
    }

    return Boolean(stay.customAddressLine1 || stay.customLocationName || stay.customTitle || stay.customDescription);
}

type TourItineraryProp = {
    defaultEditingEnabled?: boolean;
    showEditingToggle?: boolean;

    // externe Regel (Zeit/Status), zusätzlich zu Entitlement
    editingAllowed?: boolean;
    editingDisabledTooltip?: string;
    onHighlightedStayChange?: (stayId: number | null) => void;
    onHoveredStayChange?: (stayId: number | null) => void;
    hoveredStayId?: number | null;
};

type TimelineEntry = {
    stay: StayDetail;
    originalIndex: number;
    anonymized: boolean;
};

type CustomStayPayload = {
    stayType: StayDetail.stayType;
    customTitle?: string;
    customDescription?: string;
    customAddressLine1: string;
    customAddressLine2?: string;
    customZipCode: string;
    customCity: string;
    customCoordinatesX: number;
    customCoordinatesY: number;
    fromDate?: number;
    toDate?: number;
};

type OverlapEffect = {
    stayId?: number;
    label: string;
    action: "delete" | "adjust";
};

function getBoundaryRank(stay?: StayDetail): number {
    if (stay?.stayType === CUSTOM_STAY_TYPE.CUSTOM_START) {
        return 0;
    }

    if (stay?.stayType === CUSTOM_STAY_TYPE.CUSTOM_END) {
        return 2;
    }

    return 1;
}

function getStayDisplayLabel(stay: StayDetail): string {
    if (stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_START) {
        return "Startort";
    }

    if (stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_END) {
        return "Zielort";
    }

    if (stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_STOP) {
        return stay.customTitle || stay.customLocationName || stay.customAddressLine1 || "Custom Stop";
    }

    return stay.stayCompound?.name || stay.staySite?.label || `Host Stay ${stay.stayId ?? ""}`.trim();
}

function getCustomStopOverlapEffects(stays: StayDetail[], fromDate: number, toDate: number): OverlapEffect[] {
    return stays
        .filter((stay) => stay.fromDate != null && stay.toDate != null)
        .filter((stay) => fromDate < stay.toDate! && toDate > stay.fromDate!)
        .map((stay) => {
            const fullyCovered = stay.fromDate! >= fromDate && stay.toDate! <= toDate;

            return {
                stayId: stay.stayId,
                label: getStayDisplayLabel(stay),
                action: fullyCovered ? "delete" : "adjust",
            };
        });
}

const DEFAULT_EDIT_LOCK_HOURS_BEFORE_ARRIVAL = 48;

export default function TourItinerary({
                                            editingAllowed = true,
                                         editingDisabledTooltip,
                                           onHighlightedStayChange,
                                           onHoveredStayChange,
                                           hoveredStayId = null,
                                         }: TourItineraryProp) {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const choosedTourDetail = useAppSelector((state) => state.tour.choosedTourDetail);
    const detailTourDistancesList = useAppSelector((state) => state.tour.detailedTourDistances);
    const loading = useAppSelector((state) => state.tour.loading);
    const isAuthenticated = useAppSelector((state) => state.login.isAuthenticated === true);
    const userRole = useAppSelector((state) => state.login.userRoleLogin);
    const vanliferId = useAppSelector((state) => state.user.vanliferId);

    const userEntitlements = useAppSelector((state) => state.user.entitlements);
    const canUseEditor = useMemo(
        () => userEntitlements.some((e) => e.featureId === "vanliferuseeditor" && e.value === "true"),
        [userEntitlements]
    );
    const stays = useMemo<StayDetail[]>(() => choosedTourDetail?.stays ?? [], [choosedTourDetail?.stays]);

    const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");
    const [activeEditStayId, setActiveEditStayId] = useState<number | null>(null);
    const [dialogStayType, setDialogStayType] = useState<StayDetail.stayType | null>(null);
    const [editingCustomStay, setEditingCustomStay] = useState<StayDetail | null>(null);
    const [pendingOverlapEffects, setPendingOverlapEffects] = useState<OverlapEffect[]>([]);
    const pendingOverlapConfirmationRef = useRef<{
        execute: () => Promise<void>;
        resolve: () => void;
        reject: (error: Error) => void;
    } | null>(null);

    const isOwnTour = useMemo(() => {
        if (!choosedTourDetail?.vanliferId || !vanliferId) {
            return false;
        }

        return choosedTourDetail.vanliferId === vanliferId;
    }, [choosedTourDetail?.vanliferId, vanliferId]);

    const isAuthenticatedVanlifer = isAuthenticated && userRole === User.userRole.VANLIFER;
    const canSeePrivateCustomStays = isOwnTour && isAuthenticatedVanlifer;

    const isTourCompleted = Boolean((choosedTourDetail as any)?.tourCompleted);
    const canManageCustomStays = isOwnTour && isAuthenticatedVanlifer && canUseEditor && !isTourCompleted && editingAllowed;

    const baseEditDisabledReason = useMemo(() => {
        if (!isAuthenticatedVanlifer) {
            return "Bearbeiten nicht verfügbar";
        }

        if (!canUseEditor) {
            return "Fehlende Berechtigung (vanliferuseeditor)";
        }

        if (isTourCompleted) {
            return "Abgeschlossene Touren können nicht bearbeitet werden";
        }

        if (!editingAllowed) {
            return editingDisabledTooltip ?? "Bearbeiten ist aktuell nicht möglich";
        }

        return null;
    }, [
        canUseEditor,
        editingAllowed,
        editingDisabledTooltip,
        isAuthenticatedVanlifer,
        isTourCompleted,
    ]);

    const stayEditStateById = useMemo(() => {
        const now = Date.now();
        const stateById: Record<number, { canActivate: boolean; tooltip: string }> = {};

        stays.forEach((stay) => {
            if (typeof stay.stayId !== "number") {
                return;
            }

            if (!isOwnTour) {
                stateById[stay.stayId] = {
                    canActivate: false,
                    tooltip: "Nur eigene Touren können bearbeitet werden",
                };
                return;
            }

            if (baseEditDisabledReason) {
                stateById[stay.stayId] = {
                    canActivate: false,
                    tooltip: baseEditDisabledReason,
                };
                return;
            }

            const stayStart = typeof stay.fromDate === "number" ? stay.fromDate : null;
            if (!stayStart) {
                stateById[stay.stayId] = {
                    canActivate: false,
                    tooltip: "Aufenthaltsdaten sind unvollständig",
                };
                return;
            }

            const lockHours =
                stay.stayCompound?.editLockHoursBeforeArrival && stay.stayCompound.editLockHoursBeforeArrival > 0
                    ? stay.stayCompound.editLockHoursBeforeArrival
                    : DEFAULT_EDIT_LOCK_HOURS_BEFORE_ARRIVAL;

            const lockWindowStart = stayStart - lockHours * 60 * 60 * 1000;
            if (now >= lockWindowStart) {
                stateById[stay.stayId] = {
                    canActivate: false,
                    tooltip: `Ab ${lockHours}h vor Aufenthaltsbeginn sind keine Änderungen mehr möglich`,
                };
                return;
            }

            stateById[stay.stayId] = {
                canActivate: true,
                tooltip: "Bearbeiten nicht verfügbar",
            };
        });

        return stateById;
    }, [baseEditDisabledReason, isOwnTour, stays]);

    useEffect(() => {
        if (activeEditStayId == null) {
            return;
        }

        if (!stayEditStateById[activeEditStayId]?.canActivate) {
            setActiveEditStayId(null);
        }
    }, [activeEditStayId, stayEditStateById]);

    useEffect(() => {
        onHighlightedStayChange?.(editingCustomStay?.stayId ?? activeEditStayId ?? null);
    }, [activeEditStayId, editingCustomStay?.stayId, onHighlightedStayChange]);

    const sortedStays = useMemo(() => {
        return [...stays].sort((a, b) => {
            const boundaryRankDifference = getBoundaryRank(a) - getBoundaryRank(b);
            if (boundaryRankDifference !== 0) {
                return boundaryRankDifference;
            }

            const dateA = new Date(a.fromDate ?? 0).getTime();
            const dateB = new Date(b.fromDate ?? 0).getTime();

            return sortDirection === "ASC"
                ? dateA - dateB
                : dateB - dateA;
        });
    }, [stays, sortDirection]);

    const timelineEntries = useMemo<TimelineEntry[]>(() => {
        return sortedStays.flatMap<TimelineEntry>((stay, originalIndex) => {
            if (!isCustomStayRecord(stay)) {
                return [{ stay, originalIndex, anonymized: false }];
            }

            if (canSeePrivateCustomStays) {
                return [{ stay, originalIndex, anonymized: false }];
            }

            if (stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_START || stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_END) {
                return [];
            }

            return [{ stay, originalIndex, anonymized: true }];
        });
    }, [canSeePrivateCustomStays, sortedStays]);

    const customStart = useMemo(() => stays.find((stay) => stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_START), [stays]);
    const customEnd = useMemo(() => stays.find((stay) => stay.stayType === CUSTOM_STAY_TYPE.CUSTOM_END), [stays]);
    const unavailableCustomTypes = useMemo(() => {
        const unavailable: StayDetail.stayType[] = [];
        if (customStart) {
            unavailable.push(CUSTOM_STAY_TYPE.CUSTOM_START);
        }
        if (customEnd) {
            unavailable.push(CUSTOM_STAY_TYPE.CUSTOM_END);
        }
        return unavailable;
    }, [customEnd, customStart]);

    const openCreateDialog = () => {
        setEditingCustomStay(null);
        setDialogStayType(CUSTOM_STAY_TYPE.CUSTOM_STOP);
    };

    const openEditDialog = (stay: StayDetail) => {
        setEditingCustomStay(stay);
        setDialogStayType(stay.stayType ?? CUSTOM_STAY_TYPE.CUSTOM_STOP);
    };

    const closeDialog = () => {
        setEditingCustomStay(null);
        setDialogStayType(null);
    };

    const persistCustomStay = async (payload: CustomStayPayload, resolvedFromDate: number, resolvedToDate: number) => {
        if (!choosedTourDetail?.tourId) {
            return;
        }

        if (editingCustomStay?.stayId) {
            await dispatch(asyncUpdateCustomStay({
                tourId: choosedTourDetail.tourId,
                stayId: editingCustomStay.stayId,
                stayType: payload.stayType,
                customTitle: payload.customTitle,
                customDescription: payload.customDescription,
                customAddressLine1: payload.customAddressLine1,
                customAddressLine2: payload.customAddressLine2,
                customZipCode: payload.customZipCode,
                customCity: payload.customCity,
                customCoordinatesX: payload.customCoordinatesX,
                customCoordinatesY: payload.customCoordinatesY,
                fromDate: resolvedFromDate,
                toDate: resolvedToDate,
            })).unwrap();
            return;
        }

        await dispatch(asyncAddCustomStay({
            tourId: choosedTourDetail.tourId,
            stayType: payload.stayType,
            customTitle: payload.customTitle,
            customDescription: payload.customDescription,
            customAddressLine1: payload.customAddressLine1,
            customAddressLine2: payload.customAddressLine2,
            customZipCode: payload.customZipCode,
            customCity: payload.customCity,
            customCoordinatesX: payload.customCoordinatesX,
            customCoordinatesY: payload.customCoordinatesY,
            fromDate: resolvedFromDate,
            toDate: resolvedToDate,
        })).unwrap();
    };

    const saveCustomStay = async (payload: CustomStayPayload) => {
        if (!choosedTourDetail?.tourId) {
            return;
        }

        if (!canManageCustomStays) {
            throw new Error(editingDisabledTooltip ?? baseEditDisabledReason ?? "Bearbeiten nicht verfügbar");
        }

        const referenceStays = stays.filter((stay) => stay.stayId !== editingCustomStay?.stayId);
        const routeDates = referenceStays.reduce<{ earliest?: number; latest?: number }>((accumulator, stay) => {
            if (stay.fromDate != null) {
                accumulator.earliest = accumulator.earliest == null ? stay.fromDate : Math.min(accumulator.earliest, stay.fromDate);
            }
            if (stay.toDate != null) {
                accumulator.latest = accumulator.latest == null ? stay.toDate : Math.max(accumulator.latest, stay.toDate);
            }
            return accumulator;
        }, {});

        const resolvedFromDate = payload.fromDate ?? (
            payload.stayType === CUSTOM_STAY_TYPE.CUSTOM_START && routeDates.earliest != null
                ? routeDates.earliest
                : payload.stayType === CUSTOM_STAY_TYPE.CUSTOM_END && routeDates.latest != null
                    ? routeDates.latest
                    : undefined
        );

        const resolvedToDate = payload.toDate ?? (
            payload.stayType === CUSTOM_STAY_TYPE.CUSTOM_START && routeDates.earliest != null
                ? routeDates.earliest
                : payload.stayType === CUSTOM_STAY_TYPE.CUSTOM_END && routeDates.latest != null
                    ? routeDates.latest
                    : undefined
        );

        if (resolvedFromDate == null || resolvedToDate == null) {
            throw new Error("Datumsbereich fuer Custom Stay konnte nicht ermittelt werden.");
        }

        if (payload.stayType === CUSTOM_STAY_TYPE.CUSTOM_STOP) {
            const overlapEffects = getCustomStopOverlapEffects(referenceStays, resolvedFromDate, resolvedToDate);
            if (overlapEffects.length > 0) {
                return new Promise<void>((resolve, reject) => {
                    pendingOverlapConfirmationRef.current = {
                        execute: () => persistCustomStay(payload, resolvedFromDate, resolvedToDate),
                        resolve,
                        reject,
                    };
                    setPendingOverlapEffects(overlapEffects);
                });
            }
        }

        await persistCustomStay(payload, resolvedFromDate, resolvedToDate);
    };

    const closeOverlapConfirmation = () => {
        const pendingConfirmation = pendingOverlapConfirmationRef.current;
        pendingOverlapConfirmationRef.current = null;
        setPendingOverlapEffects([]);
        pendingConfirmation?.reject(new Error("CUSTOM_STAY_OVERLAP_CANCELLED"));
    };

    const confirmOverlapResolution = async () => {
        const pendingConfirmation = pendingOverlapConfirmationRef.current;
        if (!pendingConfirmation) {
            return;
        }

        try {
            await pendingConfirmation.execute();
            pendingConfirmation.resolve();
        } catch (error) {
            pendingConfirmation.reject(error as Error);
        } finally {
            pendingOverlapConfirmationRef.current = null;
            setPendingOverlapEffects([]);
        }
    };

    const deleteManualStay = async (stayId?: number) => {
        if (!choosedTourDetail?.tourId || !stayId) {
            return;
        }

        if (!canManageCustomStays) {
            throw new Error(editingDisabledTooltip ?? baseEditDisabledReason ?? "Bearbeiten nicht verfügbar");
        }

        await dispatch(asyncDeleteCustomStay({ tourId: choosedTourDetail.tourId, stayId })).unwrap();
    };

    const isCustomStay = (stay?: StayDetail): boolean => isCustomStayRecord(stay);

    // --- Accordion state (collapsed by default) ---
    const [openCalendarStayId, setOpenCalendarStayId] = useState<number | null>(null);
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

    // Stable key to initialize collapsed state once per "stay set" (independent of sorting)
    const stayIdsKey = useMemo(() => {
        const ids = stays
            .map((s) => s.stayId)
            .filter((id): id is number => typeof id === "number")
            .sort((a, b) => a - b);

        return ids.join(",");
    }, [stays]);

    const collapseInitRef = useRef<string | null>(null);

    // Collapse all items by default when a new tour / new stay set is loaded
    useEffect(() => {
        if (!stayIdsKey) return;
        if (collapseInitRef.current === stayIdsKey) return;

        const ids = stayIdsKey
            .split(",")
            .map((v) => Number(v))
            .filter((n) => Number.isFinite(n));

        setCollapsed(new Set(ids));   // all collapsed
        setOpenCalendarStayId(null);  // close any open calendar
        collapseInitRef.current = stayIdsKey;
    }, [stayIdsKey]);

    const toggle = (stayId: number) => () => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            next.has(stayId) ? next.delete(stayId) : next.add(stayId);
            return next;
        });
    };

    const toggleStayEditMode = (stayId: number) => () => {
        if (!stayEditStateById[stayId]?.canActivate) {
            return;
        }

        setActiveEditStayId((prev) => {
            const next = prev === stayId ? null : stayId;
            setOpenCalendarStayId(next);

            if (next != null) {
                setCollapsed((collapsedPrev) => {
                    const collapsedNext = new Set(collapsedPrev);
                    collapsedNext.delete(next);
                    return collapsedNext;
                });
            }

            return next;
        });
    };

    const getDistanceTextBetween = (index: number): string | null => {
        if (!detailTourDistancesList) return null;

        // distances = stays-1 (between i and i+1)
        if (detailTourDistancesList.length === sortedStays.length - 1) {
            const d = detailTourDistancesList[index];
            return typeof d === "number" ? `${Math.round(d)} km` : null;
        }

        // legacy distances = stays
        if (detailTourDistancesList.length === sortedStays.length) {
            const d = detailTourDistancesList[index];
            return typeof d === "number" ? `${Math.round(d)} km` : null;
        }

        return null;
    };

    if (loading) return <CircularProgress />;
    if (!choosedTourDetail) return <CircularProgress />;

    if (timelineEntries.length === 0) {
        return (
            <Box sx={{ maxWidth: 800, mx: "auto", pt: 0, pr: 2, pb: 2, pl: 2 }}>
                <ItineraryHeaderControls
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    editingEnabled={false}
                    setEditingEnabled={() => undefined}
                    canUseEditor={canUseEditor}
                    editingAllowed={editingAllowed}
                    editingDisabledTooltip={editingDisabledTooltip}
                    showEditToggle={false}
                    headerAction={isOwnTour && !baseEditDisabledReason ? {
                        label: "Privaten Ort hinzufügen",
                        onClick: openCreateDialog,
                    } : null}
                />
                <Typography variant="body2" color="text.secondary">
                    Keine Aufenthalte vorhanden.
                </Typography>
            </Box>
        );
    }

    const lineColor = theme.palette.divider;
    const markerColor = theme.palette.text.secondary;
    const firstOverallStay = sortedStays[0];
    const lastOverallStay = sortedStays[sortedStays.length - 1];
    const hiddenStartDistance = !canSeePrivateCustomStays && firstOverallStay?.stayType === CUSTOM_STAY_TYPE.CUSTOM_START
        ? getDistanceTextBetween(0)
        : null;
    const hiddenEndDistance = !canSeePrivateCustomStays && lastOverallStay?.stayType === CUSTOM_STAY_TYPE.CUSTOM_END
        ? getDistanceTextBetween(sortedStays.length - 2)
        : null;

    return (
        <Box sx={{ maxWidth: 800, mx: "auto", pt: 0, pr: 2, pb: 2, pl: 2 }}>
            <ItineraryHeaderControls
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                editingEnabled={false}
                setEditingEnabled={() => undefined}
                canUseEditor={canUseEditor}
                editingAllowed={editingAllowed}
                editingDisabledTooltip={editingDisabledTooltip}
                showEditToggle={false}
                headerAction={isOwnTour && !baseEditDisabledReason ? {
                    label: "Privaten Ort hinzufügen",
                    onClick: openCreateDialog,
                } : null}
            />

            <DistanceMarker text={hiddenStartDistance ? `Anreise ${hiddenStartDistance}` : "Anreise"} color={markerColor} />

            {timelineEntries.map((entry, index) => {
                const stay = entry.stay;
                const stayId = stay.stayId!;
                const stayEditState = stayEditStateById[stayId] ?? {
                    canActivate: false,
                    tooltip: "Bearbeiten nicht verfügbar",
                };
                return (
                    <React.Fragment key={stayId}>
                        {isCustomStay(stay) ? (
                            <CustomStayTimelineItem
                                stay={stay}
                                lineColor={lineColor}
                                expanded={!collapsed.has(stayId)}
                                onToggle={toggle(stayId)}
                                canEdit={stayEditState.canActivate}
                                editDisabledTooltip={stayEditState.tooltip}
                                onEdit={() => openEditDialog(stay)}
                                onDelete={() => deleteManualStay(stay.stayId)}
                                anonymized={entry.anonymized}
                                active={editingCustomStay?.stayId === stayId}
                                hovered={hoveredStayId === stayId && editingCustomStay?.stayId !== stayId}
                                onHoverChange={(hovered: boolean) => onHoveredStayChange?.(hovered ? stayId : null)}
                            />
                        ) : (
                            <StayProposalTimelineItem
                                stay={stay}
                                lineColor={lineColor}
                                expanded={!collapsed.has(stayId)}
                                onToggle={toggle(stayId)}
                                editingEnabled={activeEditStayId === stayId}
                                showStayCalendar={openCalendarStayId === stayId}
                                onToggleEditMode={toggleStayEditMode(stayId)}
                                canActivateEditMode={stayEditState.canActivate}
                                showEditAction={isOwnTour}
                                editDisabledTooltip={stayEditState.tooltip}
                                isDirty={stay.isDirty === true}
                                hovered={hoveredStayId === stayId && activeEditStayId !== stayId}
                                onHoverChange={(hovered: boolean) => onHoveredStayChange?.(hovered ? stayId : null)}
                            />
                        )}

                        {index < timelineEntries.length - 1 && (
                            <DistanceMarker
                                text={getDistanceTextBetween(entry.originalIndex) ?? "Entfernung unbekannt"}
                                color={markerColor}
                            />
                        )}
                    </React.Fragment>
                );
            })}

            <DistanceMarker text={hiddenEndDistance ? `Abreise ${hiddenEndDistance}` : "Abreise"} color={markerColor} />

            {dialogStayType && (
                <CustomStayDialog
                    open={!!dialogStayType}
                    title={editingCustomStay ? "Reisepunkt bearbeiten" : "Reisepunkt hinzufügen"}
                    initialStayType={dialogStayType}
                    unavailableTypes={unavailableCustomTypes}
                    initialStay={editingCustomStay}
                    onClose={closeDialog}
                    onSave={saveCustomStay}
                />
            )}

            <ConfirmDialog
                open={pendingOverlapEffects.length > 0}
                onClose={closeOverlapConfirmation}
                onConfirm={confirmOverlapResolution}
                title="Bestehende Aufenthalte werden angepasst"
                confirmText="Trotzdem speichern"
                confirmColor="warning"
                icon={<WarningAmberRoundedIcon color="warning" />}
                content={
                    <Stack spacing={1}>
                        <Typography variant="body2">
                            Der neue Zwischenstopp überschneidet bestehende Reiseelemente. Damit die Tour konsistent bleibt, werden folgende Aufenthalte automatisch angepasst:
                        </Typography>
                        {pendingOverlapEffects.map((effect, index) => (
                            <Typography key={`${effect.stayId ?? effect.label}-${index}`} variant="body2">
                                {effect.label}: {effect.action === "delete" ? "wird gelöscht" : "wird angepasst"}
                            </Typography>
                        ))}
                    </Stack>
                }
            />
        </Box>
    );
}
